import { useState, useEffect, useCallback } from 'react';
import { fetchClips, fetchPublicClips } from '../services/api';

/**
 * Hook for fetching and managing clips
 */

export function useClips({ limit = 20, days = 7, broadcasterLogin = null, startDate = null, endDate = null } = {}) {

  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [warning, setWarning] = useState(null);

  const loadClips = useCallback(async (cursor = null) => {
    try {
      setLoading(true);
      setError(null);

      const result = broadcasterLogin

        ? await fetchPublicClips({ login: broadcasterLogin, cursor, limit, days, startDate, endDate })
        : await fetchClips({ cursor, limit, days, startDate, endDate });

      // Helper: extract a timestamp (ms) from a clip object using known fields
      const getClipTimestamp = (clip) => {
        const ts = clip?.clip_created_at || clip?.created_at || clip?.createdAt || clip?.clipCreatedAt || '';
        const parsed = Date.parse(ts);
        return Number.isNaN(parsed) ? 0 : parsed;
      };

      const sortByNewest = (arr) => {
        return arr.slice().sort((a, b) => getClipTimestamp(b) - getClipTimestamp(a));
      };

      // Debug: log a small sample of fetched clip timestamps to help trace ordering
      try {
        const sample = (result.data || []).slice(0, 5).map((c) => {
          const ts = c?.clip_created_at || c?.created_at || c?.createdAt || c?.clipCreatedAt || '';
          return ts;
        });
        // eslint-disable-next-line no-console
        console.info('useClips fetched page sample', { cursor, sample, nextCursor: result.nextCursor });
      } catch (e) {
        // ignore logging errors
      }


      if (cursor) {
        // Merge paginated results with existing clips, dedupe, and
        // ensure newest-first ordering. This handles cases where pages
        // may overlap or the remote pagination order is not strictly
        // newest-first across all pages.
        setClips((prev) => {
          const combined = [...prev, ...(result.data || [])];
          const seen = new Set();
          const unique = [];
          for (const c of combined) {
            // Support primitive items (tests may return simple arrays of ids)
            const key = (typeof c === 'object' && c)
              ? (c.id || c.twitch_clip_id || c.clip_id || JSON.stringify(c))
              : String(c);
            /* c8 ignore next */
            if (!seen.has(key)) {
              seen.add(key);
              unique.push(c);
            }
          }
          return sortByNewest(unique);
        });
      } else {
        // Initial load: ensure newest-first ordering
        setClips(sortByNewest(result.data || []));
      }

      setNextCursor(result.nextCursor);
      setHasMore(!!result.nextCursor);
      // Set warning if present in result (only on initial load, not on pagination)
      if (!cursor && result.warning) {
        setWarning(result.warning);
      }
    } catch (err) {
      // If server-side error (5xx), suppress the red UI notice and log instead.
      // This avoids alarming users when the API is temporarily failing.
      if (err && err.status && err.status >= 500 && err.status < 600) {
        // keep clips as-is (likely empty) and log for diagnostics
        // preserve non-fatal state
        // eslint-disable-next-line no-console
        console.error('Server error fetching clips:', err);
        setError(null);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }

  }, [limit, days, broadcasterLogin, startDate, endDate]);


  const loadMore = useCallback(() => {
    if (nextCursor && !loading) {
      loadClips(nextCursor);
    }
  }, [nextCursor, loading, loadClips]);

  const refresh = useCallback(() => {
    loadClips(null);
  }, [loadClips]);

  useEffect(() => {
    loadClips(null);
  }, [loadClips]);

  return {
    clips,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
    warning,
  };
}

/**
 * Hook for managing clip selection
 * @param {number} maxSelection - Maximum number of clips that can be selected
 * @param {Array} availableClips - List of available clips for select all / invert operations
 */
export function useClipSelection(maxSelection = 5, availableClips = []) {
  const [selectedIds, setSelectedIds] = useState(new Set());

  const toggle = useCallback((clipId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(clipId)) {
        next.delete(clipId);
      } else if (next.size < maxSelection) {
        next.add(clipId);
      }
      return next;
    });
  }, [maxSelection]);

  const select = useCallback((clipId) => {
    setSelectedIds((prev) => {
      if (prev.size >= maxSelection) return prev;
      const next = new Set(prev);
      next.add(clipId);
      return next;
    });
  }, [maxSelection]);

  const deselect = useCallback((clipId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(clipId);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const selectAll = useCallback(() => {
    const availableIds = availableClips.map((clip) => clip.id);
    const toSelect = availableIds.slice(0, maxSelection);
    setSelectedIds(new Set(toSelect));
  }, [availableClips, maxSelection]);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const invertSelection = useCallback(() => {
    const availableIds = new Set(availableClips.map((clip) => clip.id));
    setSelectedIds((prev) => {
      const next = new Set();
      availableIds.forEach((id) => {
        if (!prev.has(id) && next.size < maxSelection) {
          next.add(id);
        }
      });
      return next;
    });
  }, [availableClips, maxSelection]);

  const removeSelected = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isSelected = useCallback((clipId) => {
    return selectedIds.has(clipId);
  }, [selectedIds]);

  const canSelectMore = selectedIds.size < maxSelection;

  return {
    selectedIds: Array.from(selectedIds),
    selectedCount: selectedIds.size,
    maxSelection,
    canSelectMore,
    toggle,
    select,
    deselect,
    clear,
    selectAll,
    deselectAll,
    invertSelection,
    removeSelected,
    isSelected,
  };
}

